/**
 * StudySync Researcher Dashboard Component
 * Main dashboard for researchers to manage studies and view statistics
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Container,
  CardActions,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import {
  Add,
  People,
  ContentCopy,
  Science,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  MoreVert,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSecureAuth } from "../../contexts/SecureAuthContext";
import { apiService } from "../../config/api";
import { AuthService } from "../../services/authService";
import LoadingSpinner from "../common/LoadingSpinner";
import NotificationPopup from "../common/NotificationPopup";
import { formatStudyDescription, formatStudyName } from "../../utils/textUtils";
import { useNotifications } from "../../hooks/useNotifications";
import type { Study } from "../../types";
import { Link } from 'react-router-dom';
import { analyticsService } from "../../services/analyticsService";

<Link to="/study-management/analytics" className="btn btn-outline">
  View Analytics
</Link>

const ResearcherDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useSecureAuth();
  const { notification, showSuccess, showError, clearNotification } =
    useNotifications();

  const [studies, setStudies] = useState<Study[]>([]);
  const [collaborativeStudies, setCollaborativeStudies] = useState<Study[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null);
  const [viewMode, setViewMode] = useState<"owned" | "collaborative">("owned");
  const [copiedStudyCode, setCopiedStudyCode] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Create study form
  const [newStudyName, setNewStudyName] = useState("");
  const [newStudyDescription, setNewStudyDescription] = useState("");
  const [newStudyDuration, setNewStudyDuration] = useState("");
  const [newStudyPassword, setNewStudyPassword] = useState("");
  const [newStudyCollaborators, setNewStudyCollaborators] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [studyNameError, setStudyNameError] = useState<string | null>(null);

  // Edit study form
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editStudyName, setEditStudyName] = useState("");
  const [editStudyDescription, setEditStudyDescription] = useState("");
  const [editStudyDuration, setEditStudyDuration] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [studyToEdit, setStudyToEdit] = useState<Study | null>(null);

  // Change password dialog
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const selectedStudyHasPassword = Boolean(studyToEdit?.password);
  const passwordDialogTitle = selectedStudyHasPassword
    ? "Change Study Password"
    : "Set Study Password";
  let passwordSubmitLabel = selectedStudyHasPassword
    ? "Change Password"
    : "Set Password";
  if (passwordChangeLoading) {
    passwordSubmitLabel = selectedStudyHasPassword ? "Changing..." : "Setting...";
  }

  // Join study form
  const [joinStudyCode, setJoinStudyCode] = useState("");
  const [joinStudyPassword, setJoinStudyPassword] = useState("");
  const [showJoinPassword, setShowJoinPassword] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);

  const loadStudies = useCallback(async () => {
    try {
      setLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Required",
          "Please log in to view your studies"
        );
        return;
      }

      // Load owned studies
      const ownedResponse = await AuthService.getResearcherStudies(token);
      if (ownedResponse.success && ownedResponse.data) {
        setStudies(ownedResponse.data);
      } else {
        showError(
          "Failed to Load Studies",
          ownedResponse.error || "Failed to load owned studies"
        );
      }

      // Load collaborative studies
      const collaborativeResponse = await AuthService.getCollaborativeStudies(
        token
      );
      if (collaborativeResponse.success && collaborativeResponse.data) {
        setCollaborativeStudies(collaborativeResponse.data);
      } else {
        // Don't show error for collaborative studies if user has none
        setCollaborativeStudies([]);
      }
    } catch (err: any) {
      showError(
        "Failed to Load Studies",
        "Failed to load studies: " + (err.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadStudies();
  }, [loadStudies]);

  // Handle URL parameters for pre-filling join study form
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const studyCode = urlParams.get("code");
    const studyPassword = urlParams.get("password");

    if (studyCode) {
      setJoinStudyCode(studyCode);
      if (studyPassword) {
        setJoinStudyPassword(studyPassword);
      }
      // Open the join dialog if we have a study code
      setJoinDialogOpen(true);
    }
  }, [location.search]);

  // Validate study name for Google Groups compatibility
  const validateStudyName = (name: string): string | null => {
    if (!name || name.trim().length === 0) {
      return "Study name is required";
    }

    const trimmed = name.trim();

    // Check for invalid characters that Google Groups API doesn't allow
    // Google Groups API doesn't allow: < > { } [ ] \ | ` ~ ! @ # $ % ^ & * ( ) + = \ /
    const invalidChars = /[<>{}[\]\\|`~!@#$%^&*()+=\\/]/;
    if (invalidChars.test(trimmed)) {
      const foundChars = trimmed.match(invalidChars);
      return `Invalid characters found: ${foundChars?.join(', ')}. Please remove: < > { } [ ] \\ | \` ~ ! @ # $ % ^ & * ( ) + = \\ /`;
    }

    // Check length - account for "Study: " prefix and " - CODE" suffix
    // Google Groups API limit is 100 characters total
    const maxStudyNameLength = 85;
    if (trimmed.length > maxStudyNameLength) {
      return `Study name is too long (${trimmed.length} characters). Maximum length is ${maxStudyNameLength} characters.`;
    }

    return null;
  };

  // Handle study name change with validation
  const handleStudyNameChange = (value: string) => {
    setNewStudyName(value);
    const error = validateStudyName(value);
    setStudyNameError(error);
  };

  const handleCreateStudy = async () => {
    if (!newStudyName.trim()) {
      showError("Validation Error", "Study name is required");
      return;
    }

    // Validate study name before submission
    const validationError = validateStudyName(newStudyName);
    if (validationError) {
      showError("Invalid Study Name", validationError);
      return;
    }

    try {
      setCreateLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError("Authentication Required", "Please log in to create a study");
        return;
      }

      // Parse collaborator emails
      const collaboratorEmails = newStudyCollaborators
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email.length > 0);

      const response = await AuthService.createStudy(
        {
          name: newStudyName.trim(),
          description: newStudyDescription.trim() || undefined,
          duration: newStudyDuration.trim() || undefined,
          password: newStudyPassword.trim() || undefined,
          collaborators: collaboratorEmails,
          settings: {
            allowSelfEnrollment: false,
            requireVerification: true,
            dataRetentionDays: 365,
          },
        },
        token
      );

      if (response.success) {
        closeAllDialogs();
        analyticsService.trackStudyCreated({
          studyId: response.study?.id || '',
          collaboratorCount: collaboratorEmails.length,
          hasDuration: !!newStudyDuration.trim()
        });
        showSuccess(
          "Study Created",
          `Study "${newStudyName}" has been created successfully with code: ${response.study?.studyCode}`
        );
        await loadStudies(); // Reload studies after creation
      } else {
        showError(
          "Failed to Create Study",
          response.error || "An error occurred while creating the study"
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Create Study",
        "Failed to create study: " + (err.message || "Unknown error")
      );
    } finally {
      setCreateLoading(false);
    }
  };

  const handleJoinStudy = async () => {
    if (!joinStudyCode.trim() || !joinStudyPassword.trim()) {
      showError(
        "Validation Error",
        "Please enter both study code and password",
        "Join Study"
      );
      return;
    }

    try {
      setJoinLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to join study",
          "Join Study"
        );
        return;
      }

      const response = await AuthService.joinStudy(
        {
          studyCode: joinStudyCode.trim(),
          password: joinStudyPassword.trim(),
        },
        token
      );

      if (response.success) {
        closeAllDialogs();
        showSuccess(
          "Study Joined",
          "You have successfully joined the study as a collaborator"
        );
        await loadStudies(); // Reload studies to show the new collaborative study
      } else {
        showError(
          "Failed to Join Study",
          response.error || "Invalid study code or password"
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Join Study",
        err.message || "An unexpected error occurred while joining the study"
      );
    } finally {
      setJoinLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (
      !studyToEdit ||
      (selectedStudyHasPassword && !oldPassword.trim()) ||
      !newPassword.trim() ||
      !confirmPassword.trim()
    ) {
      showError(
        "Validation Error",
        selectedStudyHasPassword
          ? "All password fields are required"
          : "New password and confirmation are required"
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      showError("Validation Error", "New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      showError(
        "Validation Error",
        "New password must be at least 6 characters long"
      );
      return;
    }

    try {
      setPasswordChangeLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Required",
          "Please log in to change password"
        );
        return;
      }

      // First verify the old password matches the current study password when one exists.
      if (selectedStudyHasPassword && studyToEdit.password !== oldPassword) {
        showError("Invalid Password", "Current password is incorrect");
        return;
      }

      const response = await AuthService.updateStudy(
        studyToEdit.id,
        {
          password: newPassword.trim(),
        },
        token
      );

      if (response.success) {
        closeAllDialogs();
        showSuccess(
          "Password Changed",
          "Study password has been updated successfully"
        );
        await loadStudies(); // Reload studies
      } else {
        showError(
          "Failed to Change Password",
          response.error || "An error occurred while changing the password"
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Change Password",
        "Failed to change password: " + (err.message || "Unknown error")
      );
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedStudy(null);
  };

  const closeAllDialogs = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setChangePasswordOpen(false);
    setJoinDialogOpen(false);
    setMenuAnchor(null);
    setSelectedStudy(null);
    setStudyToEdit(null);
    // Reset form states
    setStudyNameError(null);
    setNewStudyName("");
    setNewStudyDescription("");
    setNewStudyDuration("");
    setNewStudyPassword("");
    setNewStudyCollaborators("");
    setEditStudyName("");
    setEditStudyDescription("");
    setEditStudyDuration("");
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setJoinStudyCode("");
    setJoinStudyPassword("");
  };

  const handleUpdateStudy = async () => {
    if (!studyToEdit || !editStudyName.trim()) {
      showError("Validation Error", "Study name is required");
      return;
    }

    try {
      setEditLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError("Authentication Required", "Please log in to update study");
        return;
      }

      const response = await AuthService.updateStudy(
        studyToEdit.id,
        {
          name: editStudyName.trim(),
          description: editStudyDescription.trim() || undefined,
          duration: editStudyDuration.trim() || undefined,
        },
        token
      );

      if (response.success) {
        closeAllDialogs();
        showSuccess(
          "Study Updated",
          "Study information has been updated successfully"
        );
        await loadStudies(); // Reload studies
      } else {
        showError(
          "Failed to Update Study",
          response.error || "An error occurred while updating the study"
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Update Study",
        "Failed to update study: " + (err.message || "Unknown error")
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteStudy = async () => {
    if (!selectedStudy) return;

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError("Authentication Required", "Please log in to delete study");
        return;
      }

      const response = await AuthService.deleteStudy(selectedStudy.id, token);
      if (response.success) {
        closeAllDialogs();
        showSuccess(
          "Study Deleted",
          `Study "${selectedStudy.name}" has been deleted successfully`
        );
        await loadStudies(); // Reload studies
      } else {
        showError(
          "Failed to Delete Study",
          response.error || "Failed to delete study"
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Delete Study",
        "Failed to delete study: " + (err.message || "Unknown error")
      );
    } finally {
      handleMenuClose();
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    study: Study
  ) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedStudy(study);
  };

  const copyStudyCode = async (studyCode: string, studyId?: string) => {
    try {
      await navigator.clipboard.writeText(studyCode);
      setCopiedStudyCode(studyCode);
      analyticsService.trackStudyCodeCopied({ studyId: studyId || '', source: 'dashboard' });
      setTimeout(() => setCopiedStudyCode(null), 2000);
    } catch (err) {
      // Silently fail - user can try again
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "default";
      default:
        return "default";
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading your studies..." />;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Research Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Welcome back, {currentUser?.displayName || currentUser?.email}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            {viewMode === "collaborative" ? (
              <Button
                variant="contained"
                startIcon={<People />}
                onClick={() => setJoinDialogOpen(true)}
              >
                Join Study
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<People />}
                  onClick={() => setJoinDialogOpen(true)}
                >
                  Join Study
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCreateDialogOpen(true)}
                >
                  Create Study
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Study Tabs */}
        <Card elevation={3} sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={currentTab}
              onChange={(event, newValue) => {
                setCurrentTab(newValue);
                setViewMode(newValue === 0 ? "owned" : "collaborative");
              }}
              aria-label="study tabs"
              variant="fullWidth"
            >
              <Tab
                label={`Your Studies (${studies.length})`}
                id="study-tab-0"
                aria-controls="study-tabpanel-0"
              />
              <Tab
                label={`Collaborative Studies (${collaborativeStudies.length})`}
                id="study-tab-1"
                aria-controls="study-tabpanel-1"
              />
            </Tabs>
          </Box>

          <Box sx={{ py: 3, px: 3 }}>
            {/* Studies Grid */}
            {(viewMode === "owned" ? studies : collaborativeStudies).length ===
            0 ? (
              <Card sx={{ textAlign: "center", py: 6 }}>
                <CardContent>
                  <Science
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {viewMode === "owned"
                      ? "No Studies Yet"
                      : "No Collaborative Studies Yet"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {viewMode === "owned"
                      ? "Create your first research study to get started with participant enrollment and data collection."
                      : "Join your first collaborative study by entering a study code and password."}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={viewMode === "owned" ? <Add /> : <People />}
                    onClick={() =>
                      viewMode === "owned"
                        ? setCreateDialogOpen(true)
                        : setJoinDialogOpen(true)
                    }
                  >
                    {viewMode === "owned"
                      ? "Create Your First Study"
                      : "Join Your First Collaborative Study"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={3}>
                {(viewMode === "owned" ? studies : collaborativeStudies).map(
                  (study) => (
                    <Grid item xs={12} sm={6} md={4} key={study.id}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          "&:hover": {
                            boxShadow: 3,
                          },
                        }}
                        onClick={() =>
                          navigate(`/researcher/study/${study.id}`)
                        }
                      >
                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          {/* Line 1: Study name (max 2 lines, 28 chars each) + 3-dot menu */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Box sx={{ flexGrow: 1, mr: 1 }}>
                              {formatStudyName(study.name).lines.map(
                                (line, index) => (
                                  <Typography
                                    key={index}
                                    variant="h6"
                                    component="h2"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "1.1rem",
                                      lineHeight: 1.2,
                                      mb: index === 0 ? 0.2 : 0,
                                    }}
                                  >
                                    {line}
                                  </Typography>
                                )
                              )}
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.5,
                              }}
                            >
                              {viewMode === "collaborative" && (
                                <Chip
                                  label="Collaborator"
                                  color="primary"
                                  size="small"
                                  sx={{ fontSize: "0.7rem" }}
                                />
                              )}
                              {viewMode === "owned" && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, study)}
                                  sx={{ p: 0.5 }}
                                >
                                  <MoreVert sx={{ fontSize: 18 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>

                          {/* Line 2: Participant count and Study code + status */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1.5,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <People
                                sx={{
                                  fontSize: 16,
                                  mr: 0.5,
                                  color: "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {study.participantCount || 0} participants
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                alignItems: "center",
                              }}
                            >
                              <Chip
                                label={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <span>{study.studyCode}</span>
                                    <Tooltip title={copiedStudyCode === study.studyCode ? 'Copied!' : 'Copy'}>
                                      <Box
                                        component="span"
                                        sx={{ 
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          cursor: "pointer",
                                          ml: 0.5
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copyStudyCode(study.studyCode, study.id);
                                        }}
                                      >
                                        {copiedStudyCode === study.studyCode ? (
                                          <CheckCircle sx={{ fontSize: 12, color: 'success.main' }} />
                                        ) : (
                                          <ContentCopy sx={{ fontSize: 12 }} />
                                        )}
                                      </Box>
                                    </Tooltip>
                                  </Box>
                                }
                                variant="outlined"
                                size="small"
                                sx={{ fontSize: "0.75rem" }}
                              />
                              <Chip
                                label={study.status}
                                color={getStatusColor(study.status)}
                                size="small"
                                sx={{ fontSize: "0.75rem" }}
                              />
                            </Box>
                          </Box>

                          {/* Lines 3-6: Study description (up to 4 lines, 39 chars each) */}
                          <Box sx={{ mb: 2, minHeight: "80px" }}>
                            {study.description ? (
                              formatStudyDescription(study.description).map(
                                (line, index) => (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      lineHeight: 1.3,
                                      mb: 0.3,
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {line}
                                  </Typography>
                                )
                              )
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                No description provided
                              </Typography>
                            )}
                          </Box>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            Created:{" "}
                            {new Date(study.createdAt).toLocaleDateString()}
                          </Typography>
                        </CardContent>

                        {/* Card Actions */}
                        <CardActions
                          sx={{
                            p: 2,
                            pt: 0,
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/researcher/study/${study.id}`);
                            }}
                            variant="outlined"
                            sx={{ flex: 1, minWidth: "120px" }}
                          >
                            View Study
                          </Button>
                          {viewMode === "owned" && (
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setStudyToEdit(study);
                                setEditStudyName(study.name);
                                setEditStudyDescription(
                                  study.description || ""
                                );
                                setEditStudyDuration(study.duration || "");
                                setEditDialogOpen(true);
                              }}
                              variant="outlined"
                              sx={{ flex: 1, minWidth: "120px" }}
                            >
                              Edit Study
                            </Button>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  )
                )}
              </Grid>
            )}
          </Box>
        </Card>

        {/* Study Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleDeleteStudy} sx={{ color: "error.main" }}>
            <Delete sx={{ mr: 1 }} />
            Delete Study
          </MenuItem>
        </Menu>

        {/* Create Study Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create New Study</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Study Name"
              fullWidth
              variant="outlined"
              value={newStudyName}
              onChange={(e) => handleStudyNameChange(e.target.value)}
              error={!!studyNameError}
              helperText={
                studyNameError || 
                "Study name will be used to create a Google Group. Avoid special characters: < > { } [ ] \\ | ` ~ ! @ # $ % ^ & * ( ) + = \\ /"
              }
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description (Optional)"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={newStudyDescription}
              onChange={(e) => setNewStudyDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Study Duration (Optional)"
              fullWidth
              variant="outlined"
              value={newStudyDuration}
              onChange={(e) => setNewStudyDuration(e.target.value)}
              placeholder="e.g., 4 weeks, 2 months, 6 months"
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Study Password"
              fullWidth
              variant="outlined"
              type={showPassword ? "text" : "password"}
              value={newStudyPassword}
              onChange={(e) => setNewStudyPassword(e.target.value)}
              placeholder="Enter a secure password (minimum 6 characters)"
              helperText={
                newStudyPassword.trim() && newStudyPassword.length < 6
                  ? "Password must be at least 6 characters"
                  : undefined
              }
              error={!!(newStudyPassword.trim() && newStudyPassword.length < 6)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Collaborator Emails (Optional)"
              fullWidth
              variant="outlined"
              value={newStudyCollaborators}
              onChange={(e) => setNewStudyCollaborators(e.target.value)}
              placeholder="Enter email addresses separated by commas (e.g., collaborator1@university.edu, collaborator2@stanford.edu)"
              helperText="External collaborators will be automatically added to the study's Google Group and granted bucket access"
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateStudy}
              variant="contained"
              disabled={
                createLoading ||
                !newStudyName.trim() ||
                !!studyNameError ||
                !!(newStudyPassword.trim() && newStudyPassword.length < 6)
              }
            >
              {createLoading
                ? "Please wait 30 seconds for study to be created..."
                : "Create Study"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Study Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Study</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Study Name"
              fullWidth
              variant="outlined"
              value={editStudyName}
              onChange={(e) => setEditStudyName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description (Optional)"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={editStudyDescription}
              onChange={(e) => setEditStudyDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Study Duration (Optional)"
              fullWidth
              variant="outlined"
              value={editStudyDuration}
              onChange={(e) => setEditStudyDuration(e.target.value)}
              placeholder="e.g., 4 weeks, 2 months, 6 months"
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
            <Button
              onClick={() => setChangePasswordOpen(true)}
              variant="outlined"
              color="secondary"
            >
              Change Password
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleUpdateStudy}
                variant="contained"
                disabled={editLoading || !editStudyName.trim()}
              >
                {editLoading ? "Updating..." : "Update Study"}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{passwordDialogTitle}</DialogTitle>
          <DialogContent>
            {selectedStudyHasPassword && (
              <TextField
                autoFocus
                margin="dense"
                label="Current Password"
                fullWidth
                variant="outlined"
                type={showOldPassword ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                      >
                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <TextField
              autoFocus={!selectedStudyHasPassword}
              margin="dense"
              label="New Password"
              fullWidth
              variant="outlined"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="dense"
              label="Confirm New Password"
              fullWidth
              variant="outlined"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePasswordOpen(false)}>Cancel</Button>
            <Button
              onClick={handleChangePassword}
              variant="contained"
              disabled={
                passwordChangeLoading ||
                (selectedStudyHasPassword && !oldPassword.trim()) ||
                !newPassword.trim() ||
                !confirmPassword.trim()
              }
            >
              {passwordSubmitLabel}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Join Study Dialog */}
        <Dialog
          open={joinDialogOpen}
          onClose={() => setJoinDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Join Study as Collaborator</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the study code and password to join as a collaborating
              researcher.
            </Typography>
            <TextField
              margin="dense"
              label="Study Code"
              fullWidth
              variant="outlined"
              value={joinStudyCode}
              onChange={(e) => setJoinStudyCode(e.target.value.toUpperCase())}
              placeholder="Enter 6-character study code"
              inputProps={{ maxLength: 6 }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Study Password"
              fullWidth
              variant="outlined"
              type={showJoinPassword ? "text" : "password"}
              value={joinStudyPassword}
              onChange={(e) => setJoinStudyPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowJoinPassword(!showJoinPassword)}
                      edge="end"
                    >
                      {showJoinPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJoinDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleJoinStudy}
              variant="contained"
              disabled={
                joinLoading ||
                !joinStudyCode.trim() ||
                !joinStudyPassword.trim()
              }
            >
              {joinLoading ? "Joining..." : "Join Study"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Popup */}
        <NotificationPopup
          notification={notification}
          onClose={clearNotification}
        />
      </Box>
    </Container>
  );
};

export default ResearcherDashboard;
